import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Check, Loader2, Flame, Timer, Clock, Infinity as InfinityIcon, Waves,
  RefreshCcw, Pencil, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../../context/ClientAuthContext';
import { buildGuidedCardioProgram, TOLERANCE_MINUTES } from './cardioSelectionEngine';
import { estimateCardioSessionMinutes } from './cardioTimeEstimator';

const METHOD_ICONS = { hiit: Flame, tabata: Timer, emom: Clock, amrap: InfinityIcon, intervals: Waves };
const LEVELS = [
  { value: 'debutant', label: 'Débutant' }, { value: 'intermediaire', label: 'Intermédiaire' }, { value: 'avance', label: 'Avancé' },
];
const OBJECTIVES = [
  { id: 'perte_gras', label: 'Perte de gras / cardio brûle-graisses' },
  { id: 'endurance', label: 'Endurance cardio-vasculaire' },
  { id: 'explosivite', label: 'Explosivité & puissance' },
  { id: 'forme', label: 'Forme générale / bien-être' },
];
const EQUIPMENT_OPTIONS = [
  { id: 'poids_du_corps', label: 'Poids du corps' },
  { id: 'petit_materiel', label: 'Petit matériel (kettlebell, corde, medicine ball...)' },
  { id: 'halteres', label: 'Haltères' },
  { id: 'machine_guidee', label: 'Machine guidée (rameur, vélo, tapis...)' },
];

const Chip = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`px-3.5 py-2 rounded-full text-xs font-bold border transition-colors text-left ${active
      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white border-transparent'
      : 'bg-zinc-800/60 text-zinc-300 border-white/10 hover:border-orange-500/40'}`}>
    {children}
  </button>
);

/** Parcours "Cardio guidé" — questionnaire déterministe (SANS IA), génère un programme précis
 * et immédiat (±5min) en piochant dans la vraie bibliothèque cardio, à la manière du parcours
 * débutant musculation (`CustomBeginnerFlow.jsx`). */
export const GuidedCardioFlow = ({ en, onBack, onCreated }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();

  const [loading, setLoading] = useState(true);
  const [methods, setMethods] = useState([]);
  const [library, setLibrary] = useState([]);
  const [phase, setPhase] = useState('form'); // form | recap
  const [saving, setSaving] = useState(false);
  const [seed, setSeed] = useState(0);

  const [objective, setObjective] = useState('perte_gras');
  const [name, setName] = useState('');
  const [preferredMethods, setPreferredMethods] = useState([]);
  const [equipmentAllowed, setEquipmentAllowed] = useState([]);
  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const [sessionMinutes, setSessionMinutes] = useState(25);
  const [level, setLevel] = useState('intermediaire');
  const [weeksTarget, setWeeksTarget] = useState(8);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    Promise.all([
      authFetch('/api/tracking/cardio-pro/methods').then((r) => r.json()),
      authFetch('/api/tracking/cardio-pro/exercise-library').then((r) => r.json()),
    ]).then(([m, lib]) => { setMethods(m.methods || []); setLibrary(lib.exercises || []); }).finally(() => setLoading(false));
  }, [authFetch]);

  const toggleMethod = (id) => setPreferredMethods((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  const toggleEquipment = (id) => setEquipmentAllowed((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));

  const goalText = OBJECTIVES.find((o) => o.id === objective)?.label || '';

  const buildAndReview = (nextSeed = seed) => {
    const built = buildGuidedCardioProgram({
      methods, library, preferredMethods, equipmentAllowed, daysPerWeek: +daysPerWeek,
      sessionMinutes: +sessionMinutes, level, weeksTarget: +weeksTarget, seed: nextSeed,
    });
    setSessions(built);
    setPhase('recap');
  };

  const goToForm = () => {
    if (!daysPerWeek || +daysPerWeek < 1) { toast.error(L('Merci de préciser au moins 1 séance/semaine.', 'Please specify at least 1 session/week.')); return; }
    if (!sessionMinutes || +sessionMinutes < 10) { toast.error(L('Durée de séance trop courte.', 'Session duration too short.')); return; }
    if (!name.trim()) setName(`${goalText} · ${daysPerWeek}x/${L('sem.', 'wk')}`);
    buildAndReview(seed);
  };

  const regenerate = () => { const next = seed + 1; setSeed(next); buildAndReview(next); };

  const totalMinutes = useMemo(() => sessions.reduce((sum, s) => sum + estimateCardioSessionMinutes(s), 0), [sessions]);

  const submit = () => {
    setSaving(true);
    const body = { name: name.trim() || goalText, goal: goalText, weeks_target: +weeksTarget, sessions };
    authFetch('/api/tracking/cardio-pro', { method: 'POST', body: JSON.stringify(body) })
      .then((r) => (r.ok ? r.json() : r.json().then((j) => Promise.reject(j))))
      .then(() => { toast.success(L('Programme cardio créé !', 'Cardio program created!')); onCreated(); })
      .catch(() => toast.error(L('Erreur lors de la création.', 'Error creating the program.')))
      .finally(() => setSaving(false));
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>;

  if (phase === 'recap') {
    return (
      <div className="space-y-5 pb-6" data-testid="guided-cardio-recap">
        <button onClick={() => setPhase('form')} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="guided-cardio-recap-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Modifier', 'Edit')}
        </button>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h2 className="text-lg font-black text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-orange-400" /> {name || goalText}</h2>
            <p className="text-xs text-zinc-500">{goalText} · {daysPerWeek}x/{L('semaine', 'week')} · {weeksTarget} {L('semaines', 'weeks')}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black bg-orange-500/15 text-orange-300 border border-orange-500/30" data-testid="guided-cardio-total-time-badge">
            <Clock className="w-3.5 h-3.5" /> {L(`~${totalMinutes} min/sem.`, `~${totalMinutes} min/wk`)}
          </span>
        </div>

        <div className="space-y-3">
          {sessions.map((s, i) => {
            const estMin = estimateCardioSessionMinutes(s);
            const within = Math.abs(estMin - sessionMinutes) <= TOLERANCE_MINUTES;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-4 space-y-2.5" data-testid={`guided-cardio-session-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-white font-bold text-sm">{s.session_label}</p>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${within ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'}`} data-testid={`guided-cardio-session-time-${i}`}>
                    <Clock className="w-3 h-3" /> {L(`~${estMin} min`, `~${estMin} min`)}
                  </span>
                </div>
                {s.blocks.map((b, bi) => {
                  const Icon = METHOD_ICONS[b.method] || Flame;
                  const methodLabel = methods.find((m) => m.id === b.method)?.label || b.method;
                  return (
                    <div key={bi} className="flex items-start gap-2 text-xs text-zinc-400">
                      <Icon className="w-3.5 h-3.5 text-orange-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-orange-300 font-semibold">{methodLabel}</span> · {b.method === 'amrap' ? `${Math.round((b.time_cap_seconds || b.work_seconds) / 60)} min AMRAP` : `${b.rounds} ${L('tours', 'rounds')} · ${b.work_seconds}s/${b.rest_seconds}s`}
                        <div className="text-zinc-500">{b.exercises.map((e) => e.name).join(', ')}</div>
                      </div>
                    </div>
                  );
                })}
              </motion.div>
            );
          })}
        </div>

        <div className="flex items-center gap-3 pt-2 flex-wrap">
          <button onClick={submit} disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-105 transition-transform disabled:opacity-60"
            data-testid="guided-cardio-create-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {L('Créer le programme', 'Create program')}
          </button>
          <button onClick={regenerate} className="inline-flex items-center gap-1.5 px-4 py-3 rounded-xl border border-white/15 text-zinc-300 text-sm font-semibold hover:border-orange-500/40 transition-colors" data-testid="guided-cardio-regenerate-btn">
            <RefreshCcw className="w-4 h-4" /> {L('Régénérer', 'Regenerate')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="guided-cardio-form">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="guided-cardio-back-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <h2 className="text-xl font-black text-white flex items-center gap-2"><Flame className="w-5 h-5 text-orange-400" /> {L('Cardio guidé', 'Guided Cardio')}</h2>
      <p className="text-xs text-zinc-500">{L('Répondez à quelques questions, votre programme précis apparaît immédiatement (aucune attente, aucune IA).', 'Answer a few questions, your precise program appears instantly (no wait, no AI).')}</p>

      <div>
        <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Votre objectif', 'Your goal')}</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {OBJECTIVES.map((o) => (
            <Chip key={o.id} active={objective === o.id} onClick={() => setObjective(o.id)} testId={`guided-cardio-objective-${o.id}`}>{o.label}</Chip>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block flex items-center gap-1.5"><Pencil className="w-3 h-3" /> {L('Nom du programme (optionnel)', 'Program name (optional)')}</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={L('Laissez vide pour un nom automatique', 'Leave empty for an automatic name')}
          className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600 focus:border-orange-500/50 focus:outline-none" data-testid="guided-cardio-name-input" />
      </div>

      <div>
        <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Méthodes préférées (optionnel)', 'Preferred methods (optional)')}</label>
        <div className="flex items-center gap-2 flex-wrap">
          {methods.map((m) => {
            const Icon = METHOD_ICONS[m.id] || Flame;
            const active = preferredMethods.includes(m.id);
            return (
              <button key={m.id} onClick={() => toggleMethod(m.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${active ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white border-transparent' : 'bg-zinc-800/60 text-zinc-300 border-white/10 hover:border-orange-500/40'}`}
                data-testid={`guided-cardio-method-chip-${m.id}`}>
                <Icon className="w-3.5 h-3.5" /> {m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Matériel disponible (optionnel, vide = tout)', 'Available equipment (optional, empty = all)')}</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {EQUIPMENT_OPTIONS.map((eq) => (
            <Chip key={eq.id} active={equipmentAllowed.includes(eq.id)} onClick={() => toggleEquipment(eq.id)} testId={`guided-cardio-equipment-chip-${eq.id}`}>{eq.label}</Chip>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Séances / semaine', 'Sessions / week')}</label>
          <input type="number" min={1} max={7} value={daysPerWeek} onChange={(e) => setDaysPerWeek(e.target.value)}
            className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500/50 focus:outline-none" data-testid="guided-cardio-days-input" />
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Durée séance cible (min)', 'Target session duration (min)')}</label>
          <input type="number" min={10} max={90} value={sessionMinutes} onChange={(e) => setSessionMinutes(e.target.value)}
            className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500/50 focus:outline-none" data-testid="guided-cardio-session-minutes-input" />
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Niveau', 'Level')}</label>
          <select value={level} onChange={(e) => setLevel(e.target.value)}
            className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500/50 focus:outline-none" data-testid="guided-cardio-level-select">
            {LEVELS.map((lv) => <option key={lv.value} value={lv.value}>{lv.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Durée programme (sem.)', 'Program duration (wks)')}</label>
          <input type="number" min={1} max={52} value={weeksTarget} onChange={(e) => setWeeksTarget(e.target.value)}
            className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500/50 focus:outline-none" data-testid="guided-cardio-weeks-input" />
        </div>
      </div>

      <button onClick={goToForm}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-[1.02] transition-transform"
        data-testid="guided-cardio-generate-btn">
        <Sparkles className="w-4 h-4" /> {L('Voir mon programme cardio', 'See my cardio program')}
      </button>
    </div>
  );
};

export default GuidedCardioFlow;
